// Revisión de lo leído: MESA DE COTEJO. A la izquierda el recibo tal cual; a la derecha lo que se leyó (sin IA o con
// la IA), editable celda por celda, con los datos dudosos resaltados. Arriba, "Pídele a la IA que corrija" (propone, no
// aplica). Sin lectura (foto sin IA o recibo ilegible): captura a mano de los últimos 12 meses. Arriba de la tabla, el
// estado de CADA archivo según la última lectura (contrato v2: borrador.archivos_leidos).
// Etapa 4 (Diego, 12-oct): "después de leer, el vendedor revisa, autoriza o corrige la tabla antes de calcular". Una
// tabla recién leída sale POR AUTORIZAR (el servidor no calcula con ella): «Autorizar tabla», o «Guardar y autorizar»
// con las correcciones, la deja autorizada a su nombre y se calculan las opciones.
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { BadgeCheck, CalendarPlus, ChevronDown, ExternalLink, Eye, Lock, Plus, RotateCcw, Save, ScanText, Sparkles, Trash2 } from 'lucide-react';
import { BOTON_CURVA_EN_CASA, ESPERA_SUGERIR_MS, MOTIVO_SUGERIR_IA, formatoPara, PREFIJO_CURVA_SONORA, TARIFAS, aplicarEdiciones, lecturaDeRecibos, porqueEscritosVanComoRecibo, esAvisoDePeriodosQuitados, esAvisoDeProyeccionFallida, esProyectadoConIA, mesesProyectados, fechaHermosillo, fechaHoraCorta, iguales, lecturaPorAutorizar, numero, periodosDistintos, ventanaDelAnio, vieneDelRecibo, fuenteDePago } from '@cotizador/compartido';
import type { ArchivoSubido, EdicionCampo, LecturaRecibo, Medidor, PeriodoCalculo, ResultadoCorregir } from '@cotizador/compartido';
import { TablaAjusteConsumos } from './TablaAjusteConsumos.tsx';
import { CartelFacturacion } from './CartelFacturacion.tsx';
import { api } from '../../api/consultas.ts';
import { usarUsuario } from '../../sesion.tsx';
import { Enlace, navegar } from '../../ruteo.tsx';
import { Boton, CampoArea, CampoSelect, CampoTexto, Chip, Dialogo, ErrorEnLinea, Seccion, Vacio } from '../../componentes/ui.tsx';
import { VisorArchivo, usarUrlArchivo } from '../../componentes/archivos.tsx';
import { etiquetaRuta, valorCorto } from '../../logica/ediciones.ts';
import { dinero, kwh as fmtKwh } from '../../logica/etiquetas.ts';
import { calculoDeFila, cambiosDeLaForma, conQueSeEstimo, edicionesDeLectura, filaEnBlanco, filaEstimada, filaSinRecibo, filasParaCapturar, formaDeLectura, lecturaConDatos, lecturaManual, pagoDeFila, periodoCorto, AYUDA_CORREGIR_IA, PISTA_CORREGIR_IA } from '../../logica/lectura.ts';
import { agregarPeriodoHaceFalta, avisoDeMeses, avisoSinPlan, bloqueoDelAnio, conCurvaEnLugarDeIA, conMesesQueFaltan, filaNueva, filasEnBlanco, mesesDeLaIAEnLaTabla, pedirSugerencia, periodosPedidos, planDeLaTabla, sugerirConIA } from '../../logica/completar-anio.ts';
import type { SugerenciaIA } from '../../logica/completar-anio.ts';
import { ErrorDeApi } from '../../api/cliente.ts';
import type { ErroresLectura, FilaEditable, FormaLectura } from '../../logica/lectura.ts';
import { cuentaPorEstado, estadoDeArchivo, recibosSinLeer, resumenEstados, textoEstado } from '../../logica/archivos-leidos.ts';
import { usarCotizacion } from './contexto.tsx';

const ESTADO_LECTURA: Record<LecturaRecibo['estado'], { texto: string; tono: 'verde' | 'ambar' | 'rojo' | 'neutro' }> = {
  leido: { texto: 'Leído', tono: 'verde' },
  parcial: { texto: 'Leído en parte', tono: 'ambar' },
  ilegible: { texto: 'No se pudo leer', tono: 'rojo' },
  sin_leer: { texto: 'Sin leer', tono: 'neutro' },
};

const PRIVADOS: { k: keyof LecturaRecibo['privados']; t: string }[] = [
  { k: 'titular', t: 'Titular' },
  { k: 'numero_servicio', t: 'Número de servicio' },
  { k: 'direccion', t: 'Dirección' },
  { k: 'rmu', t: 'RMU' },
  { k: 'cuenta', t: 'Cuenta' },
  { k: 'numero_medidor', t: 'Número de medidor' },
];

const OPCIONES_TARIFA = [{ valor: '', texto: 'Sin leer' }, ...TARIFAS.map((t) => ({ valor: t, texto: t }))];

/** El desglose del recibo en palabras (verificación adversarial de la etapa 4: salía «scnmem», «cenace», «distribucion»). */
const ETIQUETA_CARGO: Readonly<Record<string, string>> = {
  suministro: 'Suministro',
  distribucion: 'Distribución',
  transmision: 'Transmisión',
  cenace: 'Operación del mercado (CENACE)',
  generacion: 'Generación',
  capacidad: 'Capacidad',
  scnmem: 'Servicios conexos',
  cargo_fijo: 'Cargo fijo',
  energia: 'Energía',
  baja_tension_2pct: '2 % por baja tensión',
  factor_potencia: 'Factor de potencia',
  dap: 'Alumbrado público (DAP)',
  apoyo_estatal: 'Apoyo del gobierno del estado',
  subtotal: 'Subtotal',
  iva: 'IVA',
  total_periodo: 'Total del periodo',
};
const ETIQUETA_BLOQUE: Readonly<Record<string, string>> = {
  basico: 'Básico',
  intermedio_1: 'Intermedio 1',
  intermedio_2: 'Intermedio 2',
  intermedio: 'Intermedio',
  excedente: 'Excedente',
  base: 'Base',
  intermedia: 'Intermedia',
  punta: 'Punta',
  unico: 'Único',
};

/** ¿Este dato está en amarillo? (también si lo está todo su renglón: "/historial/3" cubre "/historial/3/consumo_kwh"). */
function dudosoEn(dudosos: ReadonlySet<string>): (ruta: string) => boolean {
  return (ruta) => dudosos.has(ruta) || [...dudosos].some((d) => ruta.startsWith(`${d}/`));
}

/** Los datos en amarillo que esta pantalla SÍ enseña (el encabezado solo cuenta esos). */
function dudosoVisible(d: string, mt: boolean): boolean {
  return /^\/historial\/\d+(\/(consumo_kwh|importe|demanda_kw))?$/.test(d) || ['/total_a_pagar', '/tarifa', '/cargos_ultimo_periodo'].includes(d) || d.startsWith('/privados/') || (mt && d === '/demanda_contratada_kw');
}

/** Lleva la vista al principio de la tabla del historial (lo primero que hay que revisar), no al botón de autorizar: el
 *  encabezado de la tabla queda justo debajo de la barra de secciones (que se queda fija arriba). */
export function irALaTabla(): void {
  const t = document.querySelector<HTMLElement>('.tabla-envoltura--historial');
  if (!t || typeof window.scrollTo !== 'function') return;
  const suave = typeof window.matchMedia === 'function' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // La barra de secciones se pega arriba al desplazarse: donde queda PEGADA (su `top` fijo + su alto), no donde está ahora.
  const barra = document.querySelector<HTMLElement>('.secciones');
  const tope = barra ? (Number.parseFloat(getComputedStyle(barra).top) || 0) + barra.getBoundingClientRect().height : 0;
  window.scrollTo({ top: Math.max(0, window.scrollY + t.getBoundingClientRect().top - tope), behavior: suave ? 'smooth' : 'auto' });
}

function Celda({ valor, alCambiar, error, dudoso, editado, etiqueta, modo = 'decimal', ancho, columna, placeholder }: { valor: string; alCambiar: (v: string) => void; error?: string; dudoso?: boolean; editado?: boolean; etiqueta: string; modo?: 'decimal' | 'text'; ancho?: 'chico' | 'normal'; columna?: 'periodo' | 'kwh' | 'demanda' | 'importe'; placeholder?: string }) {
  return (
    <input
      className={['celda', dudoso ? 'es-dudoso' : '', editado ? 'es-editado' : '', error ? 'es-error' : '', ancho === 'chico' ? 'celda--chica' : '', columna ? `celda--${columna}` : ''].join(' ')}
      inputMode={modo}
      value={valor}
      aria-label={etiqueta}
      aria-invalid={error ? true : undefined}
      placeholder={placeholder ?? '—'}
      title={error ?? (dudoso ? 'No se leyó con seguridad: confírmalo contra el recibo.' : undefined)}
      onChange={(e) => alCambiar(e.target.value)}
    />
  );
}

/** Tabla del historial (sirve para editar una lectura y para capturar a mano). */
function TablaHistorial({
  facturacion,
  filas,
  setFilas,
  errores,
  esDudoso,
  original,
  mediaTension,
  puedeEditar,
  periodos = null,
  ocultos = null,
  cuentan = null,
  baseFutura = null,
  revisar = null,
  pdfComoRecibo = null,
}: {
  facturacion: 'mensual' | 'bimestral' | null;
  filas: FilaEditable[];
  setFilas: (f: FilaEditable[]) => void;
  errores: ErroresLectura;
  esDudoso: (ruta: string) => boolean;
  original: LecturaRecibo | null;
  mediaTension: boolean;
  puedeEditar: boolean;
  /** Los periodos de ESTE medidor como los calculó el motor (pago estimado con la tarifa); null sin cálculo. */
  periodos?: readonly PeriodoCalculo[] | null;
  /** Tanda 1c: renglones que NO se enseñan (los guardados más viejos que el año que se cotiza; el resumen dice cuántos). */
  ocultos?: ReadonlySet<number> | null;
  /** Los que cuentan en el Total (los del año que se cotiza, como el resumen y el motor); null = todos. */
  cuentan?: ReadonlySet<number> | null;
  /** ADENDA z52: el cálculo usa otra base que el recibo (negocio en PDBT o aumento): dos columnas más, solo de lectura. */
  baseFutura?: { tarifa: string; aumento: boolean } | null;
  /** hoy85: renglones cuyas fechas hay que revisar (el cotizador no puede completar el año con ellos): se ven en rojo. */
  revisar?: ReadonlySet<number> | null;
  /** hoy85 (z62(1)): por qué ESTE PDF imprime como recibo los meses escritos a mano sin recibo (null = los imprime «estimado»). */
  pdfComoRecibo?: string | null;
}) {
  const poner = (i: number, k: keyof FilaEditable, v: string) => setFilas(filas.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  const orig = (f: FilaEditable) => (f.idx !== null && original ? (original.historial[f.idx] ?? null) : null);
  const enTotal = (i: number) => !cuentan || cuentan.has(i);
  const totalKwh = filas.reduce((s, f, i) => s + (enTotal(i) ? Number(f.kwh.replace(/,/g, '')) || 0 : 0), 0);
  // 21-oct: el pago de los renglones con kWh cambiados lo pone el motor con la tarifa (no se escribe); el Total suma los
  // pagos del recibo y los estimados. Los que se calculan al guardar no entran y el Total lo dice.
  const desdeRecibo = !!original?.historial.some(vieneDelRecibo);
  const pagos = filas.map((f) => pagoDeFila(f, orig(f), periodos, desdeRecibo));
  const totalImp = filas.reduce((s, f, i) => {
    if (!enTotal(i)) return s;
    const p = pagos[i]!;
    return s + (p.tipo === 'estimado' ? p.monto : p.tipo === 'al_guardar' ? 0 : Number(f.importe.replace(/[,$]/g, '')) || 0);
  }, 0);
  const pendientes = pagos.filter((p) => p.tipo === 'al_guardar').length;
  // Cada columna mide al menos lo que su cifra más larga (verificación adversarial de la etapa 4: a 390 px se veía «1430.6»
  // por 1430.67 y «88063.(»). Si la tabla no cabe, se desplaza de lado dentro de su caja; ninguna cifra se corta.
  const largo = (xs: readonly string[]) => Math.max(0, ...xs.map((x) => x.length));
  const anchos = {
    // Solo las etiquetas que se escriben en una celda (las que tienen fechas se ven cortas y no se editan).
    '--largo-periodo': largo(filas.filter((f) => !periodoCorto(f.inicio, f.fin)).map((f) => f.etiqueta)),
    '--largo-kwh': largo(filas.map((f) => f.kwh)),
    '--largo-demanda': largo(filas.map((f) => f.demanda)),
    '--largo-importe': largo(filas.map((f) => f.importe)),
  } as CSSProperties;
  return (
    <div className="tabla-envoltura tabla-envoltura--historial">
      <table className="tabla tabla--historial" style={anchos}>
        <thead>
          <tr>
            <th scope="col">Periodo</th>
            <th scope="col" className="num">
              {facturacion === 'bimestral' ? 'kWh del bimestre' : facturacion === 'mensual' ? 'kWh del mes' : 'kWh'}
            </th>
            {mediaTension && (
              <th scope="col" className="num">
                kW
              </th>
            )}
            <th scope="col" className="num">
              Importe
            </th>
            {baseFutura?.aumento && (
              <th scope="col" className="num">
                kWh con el aumento
              </th>
            )}
            {baseFutura?.aumento && (
              // D3 (verificación del 28-oct): «sin paneles» va en la tarifa del recibo; sin aumento es el recibo mismo (no hay columna)
              <th scope="col" className="num">
                Pagaría sin paneles (con el aumento)
              </th>
            )}
            {puedeEditar && (
              <th scope="col">
                <span className="solo-lector">Quitar</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {filas.map((f, i) => {
            if (ocultos?.has(i)) return null;
            const o = orig(f);
            const d = (campo: string) => f.idx !== null && esDudoso(`/historial/${f.idx}/${campo}`);
            const editadoNum = (v: string, x: number | null | undefined) => o !== null && v !== (x === null || x === undefined ? '' : String(x));
            // z25b: kWh cambiados por el vendedor (a mano o con la IA) → lo que cobró el recibo ya no vale y el pago de ese
            // periodo se estima con las cuotas de CFE. Que se vea en la tabla, con lo que decía el recibo al pasar el dedo.
            const aj = o?.ajuste_vendedor ?? null;
            const pago = pagos[i]!;
            // ADENDA z60/z61: lo que estimó «Sugerir con IA» (guardado, o todavía en la tabla): UNA marca «estimado» (kWh y pago).
            const completado = filaEstimada(f, o);
            // ADENDA z61: un periodo que puso el cotizador (sin recibo) y que todavía no trae kWh: se escribe o se sugiere. En la
            // captura a mano (sin lectura) los vacíos no cuentan: no se pintan como hueco por llenar.
            const enBlanco = !!original && filaEnBlanco(f);
            // ADENDA z62(1): un mes SIN recibo cuyos kWh escribió el vendedor: en el PDF va «estimado» (solo los meses con recibo son reales).
            const sinRecibo = filaSinRecibo(f, o, original);
            const estimado = pago.tipo !== 'recibo' && !completado && !(f.idx === null && !f.estimado);
            // hoy86 · z64-2: con qué se estimó, lo mismo en la tabla sin guardar que ya guardada (`sugerido_ia`).
            const conQue = conQueSeEstimo(f, o, original?.tarifa);
            const delCompletado = completado
              ? `${conQue} («Sugerir con IA»).${aj ? ` El recibo decía ${numero(aj.consumo_original_kwh)} kWh${aj.importe_original === null ? '' : ` (${dinero(aj.importe_original)})`}.` : o && !f.estimado ? ' No hay recibo de este periodo.' : o ? ` El recibo decía ${numero(o.consumo_kwh)} kWh${o.importe === null ? '' : ` (${dinero(o.importe)})`}.` : ' No hay recibo de este periodo.'} El pago lo estima el cotizador con la tarifa${original?.tarifa ? ` ${original.tarifa}` : ''}.`
              : undefined;
            const delRecibo = completado ? delCompletado : aj
              ? `Pago estimado con la tarifa ${original?.tarifa ?? ''}. El recibo cobró ${aj.importe_original === null ? '(sin importe)' : dinero(aj.importe_original)} por ${numero(aj.consumo_original_kwh)} kWh.`
              : pago.tipo === 'estimado' && pago.viejo && o
                ? `Pago estimado con la tarifa ${original?.tarifa ?? ''}: el importe capturado (${dinero(o.importe ?? 0)}) no corresponde a ${numero(o.consumo_kwh)} kWh.`
                : pago.tipo === 'al_guardar'
                  ? 'Cambiaste los kWh: el pago lo calcula el cotizador con la tarifa al guardar.'
                  : undefined;
            return (
              <tr key={i} className={[enBlanco ? 'es-en-blanco' : o?.origen === 'manual' ? 'es-manual' : o?.origen === 'estimado' || completado ? 'es-estimado' : '', revisar?.has(i) ? 'es-revisar' : ''].join(' ').trim() || undefined}>
                <th scope="row">
                  {/* Con fechas, el periodo se lee corto (la etiqueta de CFE no cabe en el celular) y no se edita: lo que
                      cuenta para el cálculo son las fechas. Sin fechas (historial GDMT), la etiqueta sí se edita. */}
                  {periodoCorto(f.inicio, f.fin) ? (
                    <span className="tabla__periodo" title={f.etiqueta}>
                      {periodoCorto(f.inicio, f.fin)}
                    </span>
                  ) : puedeEditar ? (
                    <Celda modo="text" columna="periodo" valor={f.etiqueta} etiqueta={`Periodo ${i + 1}`} error={errores[`fila.${i}.etiqueta`]} editado={o !== null && f.etiqueta !== o.etiqueta} alCambiar={(v) => poner(i, 'etiqueta', v)} />
                  ) : (
                    f.etiqueta
                  )}
                  {o?.origen === 'recibo' && <span className="tabla__marca">recibo</span>}
                  {/* z62(1): un mes sin recibo escrito a mano lleva UNA marca, «sin recibo · estimado» (kWh y pago), no tres. */}
                  {o?.origen === 'manual' && !sinRecibo && <span className="tabla__marca">a mano</span>}
                  {/* ADENDA z61: el periodo del año que puso el cotizador (no hay recibo de él). z62(1): con sus kWh escritos a mano, en el
                      PDF va como estimado (también los que ya se guardaron así). */}
                  {((f.idx === null && !completado && original) || (o && sinRecibo)) && (
                    <span
                      className={['tabla__marca', enBlanco || sinRecibo ? 'tabla__marca--ambar' : ''].join(' ')}
                      title={
                        // hoy85: no se promete lo que el PDF no cumple (con menos de 3 meses de recibo, baterías o dos medidores va como recibo).
                        pdfComoRecibo
                          ? `No hay recibo de este periodo${sinRecibo ? ' y sus kWh los escribiste tú' : ''}. En este PDF va como RECIBO (como antes): ${pdfComoRecibo}. El pago lo calcula el cotizador con la tarifa.`
                          : sinRecibo
                            ? 'No hay recibo de este periodo y sus kWh los escribiste tú: en el PDF va como «estimado» (solo los meses con recibo son reales). El pago lo calcula el cotizador con la tarifa.'
                            : 'No hay recibo de este periodo: escribe sus kWh o usa «Sugerir con IA». En el PDF va como «estimado». El pago lo calcula el cotizador con la tarifa.'
                      }
                    >
                      {sinRecibo ? 'sin recibo · estimado' : 'sin recibo'}
                    </span>
                  )}
                  {f.redatada && o?.inicio && (
                    <span className="tabla__marca" title={`El recibo dice ${periodoCorto(o.inicio, o.fin ?? '') ?? o.etiqueta}: es el alta del servicio, más corto que un periodo. Se toma como el periodo completo.`}>
                      alta
                    </span>
                  )}
                  {completado && (
                    <span className="tabla__marca tabla__marca--ambar" title={delCompletado}>
                      estimado
                    </span>
                  )}
                  {/* Tanda 2 (Diego, 25-oct): el mes sin recibo que proyectó la IA se ve marcado; si el vendedor lo edita, ya es «a mano». */}
                  {o && esProyectadoConIA(o) && (
                    <span className="tabla__marca tabla__marca--ambar" title={o.proyeccion_ia!.motivo}>
                      Proyectado con IA
                    </span>
                  )}
                  {estimado && !sinRecibo && (
                    <span className="tabla__marca tabla__marca--ambar" title={delRecibo}>
                      pago estimado
                    </span>
                  )}
                </th>
                <td className="num">
                  {puedeEditar ? (
                    <Celda columna="kwh" valor={f.kwh} etiqueta={`kWh de ${f.etiqueta}`} error={errores[`fila.${i}.kwh`]} dudoso={d('consumo_kwh')} editado={editadoNum(f.kwh, o?.consumo_kwh)} placeholder={enBlanco ? 'kWh' : undefined} alCambiar={(v) => poner(i, 'kwh', v)} />
                  ) : (
                    numero(Number(f.kwh) || 0)
                  )}
                </td>
                {mediaTension && (
                  <td className="num">
                    {puedeEditar ? (
                      <Celda ancho="chico" columna="demanda" valor={f.demanda} etiqueta={`Demanda de ${f.etiqueta}`} error={errores[`fila.${i}.demanda`]} dudoso={d('demanda_kw')} editado={editadoNum(f.demanda, o?.demanda_kw)} alCambiar={(v) => poner(i, 'demanda', v)} />
                    ) : (
                      f.demanda || '—'
                    )}
                  </td>
                )}
                {/* ADENDA z61 (Diego): el importe NUNCA se escribe a mano. El que imprime el recibo se queda como del recibo; los
                    demás los calcula el cotizador con la tarifa y los kWh (kWh y pesos juntos). */}
                <td className="num">
                  {pago.tipo === 'estimado' ? (
                    <span className="tabla__pago-estimado" title={delRecibo}>
                      {dinero(pago.monto)} <span className="tabla__marca tabla__marca--ambar">estimado</span>
                    </span>
                  ) : pago.tipo === 'al_guardar' ? (
                    <span className="tabla__marca tabla__marca--ambar" title={delRecibo ?? 'El pago lo calcula el cotizador con la tarifa al guardar.'}>
                      se calcula al guardar
                    </span>
                  ) : f.importe ? (
                    <span className={d('importe') ? 'tabla__importe-dudoso' : undefined} title={d('importe') ? 'No se leyó con seguridad: compáralo con el recibo. El importe no se escribe; si está mal leído, vuelve a leer el recibo.' : 'Lo que cobró CFE (del recibo).'}>
                      {dinero(Number(f.importe))}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                {baseFutura?.aumento && <td className="num">{calculoDeFila(o, periodos) ? numero(calculoDeFila(o, periodos)!.kwh) : '—'}</td>}
                {baseFutura?.aumento && <td className="num">{calculoDeFila(o, periodos) ? dinero(calculoDeFila(o, periodos)!.pago) : '—'}</td>}
                {puedeEditar && (
                  <td className="tabla__accion">
                    <button type="button" className="icono-boton icono-boton--chico" aria-label={`Quitar ${f.etiqueta}`} onClick={() => setFilas(filas.filter((_, j) => j !== i))}>
                      <Trash2 aria-hidden />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Total</th>
            <td className="num">{numero(totalKwh)}</td>
            {mediaTension && <td />}
            <td className="num" title={pendientes ? `Sin ${pendientes === 1 ? 'el periodo que se calcula' : `los ${pendientes} periodos que se calculan`} al guardar.` : undefined}>
              {totalImp ? dinero(totalImp) : '—'}
              {pendientes > 0 && <span className="tabla__marca tabla__marca--ambar">+ al guardar</span>}
            </td>
            {baseFutura?.aumento && <td className="num">{numero(filas.reduce((t, f, i) => t + (enTotal(i) ? (calculoDeFila(orig(f), periodos)?.kwh ?? 0) : 0), 0))}</td>}
            {baseFutura?.aumento && <td className="num">{dinero(filas.reduce((t, f, i) => t + (enTotal(i) ? (calculoDeFila(orig(f), periodos)?.pago ?? 0) : 0), 0))}</td>}
            {puedeEditar && <td />}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function EditorLectura({ medidor, lectura, alFaltarOtro }: { medidor: Medidor; lectura: LecturaRecibo; alFaltarOtro: (m: Medidor) => void }) {
  const { ejecutar, cot } = usarCotizacion();
  const { puedeEn } = usarUsuario();
  const puedeEditar = puedeEn('editar_cotizaciones', cot);
  // El pago estimado de cada periodo sale del cálculo guardado (con dos medidores, los de ESTE medidor).
  const resultado = cot.borrador.resultado;
  const periodosDelMedidor = resultado ? (resultado.medidores ? resultado.medidores[medidor] : medidor === 'A' ? resultado.periodos : null) : null;
  // Tanda 1c / auditoría del 26-oct (hallazgo 14): la facturación REAL, no supuesta por la tarifa: la del recibo (o la que eligió
  // el vendedor); si el recibo no la dice, la que usó el cálculo; sin cálculo, la regla del motor (GDMTO/GDMTH mensual; si no, la
  // del cuestionario).
  const q = cot.borrador.cuestionario;
  const facturacionDe = (f: FormaLectura): 'mensual' | 'bimestral' | null =>
    f.facturacion || cot.borrador.resultado?.facturacion || (f.tarifa === 'GDMTO' || f.tarifa === 'GDMTH' ? 'mensual' : q.facturacion !== 'no_se' ? q.facturacion : null);
  // hoy86 · z64-2 (verificación «vendedor» r2): el historial de la cotización dice que los kWh los sugirió la IA (antes: «Corrección a mano»).
  const motivoDeLaTabla = (f: FormaLectura) => (f.filas.some((x) => x.estimado?.ia) ? MOTIVO_SUGERIR_IA : 'Corrección a mano');
  const contexto = (f: FormaLectura) => ({ negocio: q.tipo === 'comercio' || q.tipo === 'industria', hoy: fechaHermosillo(), ampliacion: q.ampliacion.es_ampliacion, facturacion: facturacionDe(f), tarifa: f.tarifa || lectura.tarifa });
  // ADENDA z61 (Diego, 29-oct): al cargar el o los recibos (la tabla POR AUTORIZAR: recién leída o vuelta a leer) el cotizador PONE
  // los periodos que faltan para los últimos 12 meses (o 6 bimestres) hacia atrás, con sus fechas y los kWh en blanco. Una tabla ya
  // autorizada (una cotización guardada) no cambia sola (z45-2): ahí se ponen con «Poner los meses que faltan».
  const inicial = (l: LecturaRecibo): FormaLectura => {
    const f = formaDeLectura(l);
    return puedeEditar && lecturaPorAutorizar(l) ? (conMesesQueFaltan(f, l, contexto(f))?.forma ?? f) : f;
  };
  // hoy85 (verificador de hoy84): lo que el cotizador PONE al abrir (el alta tomada como el periodo completo, los meses en blanco)
  // es el estado INICIAL de la tabla, no un cambio del vendedor: antes la barra decía «1 cambio sin guardar» sin tocar nada (el
  // alta re-datada) y «Descartar» no hacía nada. Al autorizar sí se manda (el cálculo lo necesita).
  const [formaInicial, setFormaInicial] = useState<FormaLectura>(() => inicial(lectura));
  const [forma, setForma] = useState<FormaLectura>(formaInicial);
  const [errores, setErrores] = useState<ErroresLectura>({});
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // Si la lectura cambió en el servidor (otra lectura de la IA, una corrección aplicada), se vuelve a cargar la tabla.
  // Se compara DURANTE el render (no en un efecto): un efecto corría después de pintar y podía pisar lo que la persona
  // ya había tecleado (la prueba "editar una celda" lo vio: "3650" + "4000" = "36504000").
  const firma = JSON.stringify(lectura);
  const [firmaVista, setFirmaVista] = useState(firma);
  if (firma !== firmaVista) {
    const f = inicial(lectura);
    setFirmaVista(firma);
    setFormaInicial(f);
    setForma(f);
    setErrores({});
  }

  const { ediciones } = useMemo(() => edicionesDeLectura(medidor, lectura, forma, motivoDeLaTabla(forma)), [medidor, lectura, forma]);
  const sinTocar = useMemo(() => iguales(forma, formaInicial), [forma, formaInicial]);
  // hoy85 (verificador del vendedor): «lo que puso el cotizador» es cualquier diferencia entre la tabla de inicio y la lectura tal cual
  // (el alta re-datada, o los meses en blanco, que no mandan ediciones): antes, con un recibo de 2 periodos y 10 meses escritos, la
  // barra decía «1 cambio sin guardar» (contaba el reemplazo del historial) y con el de Miguel, «4 cambios».
  const delCotizador = useMemo(() => !iguales(formaInicial, formaDeLectura(lectura)), [lectura, formaInicial]);
  // El cambio de origen a "manual" acompaña a cada celda editada: no se cuenta como otro cambio. Con lo que puso el cotizador al
  // abrir (hoy85), se cuentan los renglones que cambió el VENDEDOR (lo del cotizador no es «un cambio sin guardar»).
  const cambiosVisibles = sinTocar ? 0 : delCotizador ? cambiosDeLaForma(formaInicial, forma) : ediciones.filter((e) => !e.ruta.endsWith('/origen')).length;
  const hayCambios = ediciones.length > 0 && cambiosVisibles > 0;
  const esDudoso = useMemo(() => dudosoEn(new Set(lectura.campos_dudosos)), [lectura.campos_dudosos]);
  const mt = lectura.tarifa === 'GDMTO' || lectura.tarifa === 'GDMTH' || lectura.historial.some((p) => p.demanda_kw !== null);
  // Capturada a mano (todos sus periodos los escribió alguien): no dice «Leído · De 3 archivos» (verificación adversarial).
  const aMano = lectura.historial.length > 0 && lectura.historial.every((p) => p.origen === 'manual');
  const estado = aMano ? { texto: 'Capturada a mano', tono: 'neutro' as const } : ESTADO_LECTURA[lectura.estado];
  // Solo se cuentan los datos en amarillo que la pantalla enseña (antes: «1 dato dudoso en amarillo» y nada amarillo).
  const amarillos = lectura.campos_dudosos.filter((d) => dudosoVisible(d, mt)).length;
  const desgloseDudoso = esDudoso('/cargos_ultimo_periodo');
  const privadosDudosos = PRIVADOS.filter(({ k }) => esDudoso(`/privados/${k}`)).length;

  async function guardar() {
    const r = edicionesDeLectura(medidor, lectura, forma, motivoDeLaTabla(forma));
    setErrores(r.errores);
    // z61: una tabla ya autorizada no se guarda con meses en blanco (se quedaría con el año incompleto a nombre de quien la autorizó);
    // una por autorizar sí («Solo guardar»: los meses en blanco vuelven a salir al abrirla).
    if (!porAutorizar && r.enBlanco) {
      setErrores({ ...r.errores, ...faltanEnCeldas() });
      return;
    }
    if (Object.keys(r.errores).length || !r.ediciones.length) return;
    setGuardando(true);
    setError(null);
    try {
      // Cada corrección lleva su «antes» (lo que la persona vio) y el servidor lo revisa: si la cotización cambió en otra
      // cosa, se recarga y se guarda sola, sin perder lo tecleado.
      await ejecutar((c) => api.editarBorrador(c.id, c.revision, r.ediciones), { reintentar: true });
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  // Etapa 4: autorizar la tabla (con las correcciones que haya en pantalla, en el mismo paso). Solo si hay tabla (una foto
  // ilegible sin un solo periodo no tiene nada que autorizar: el botón respondía 400).
  const porAutorizar = lecturaPorAutorizar(lectura);
  const [autorizando, setAutorizando] = useState(false);
  async function autorizar() {
    const r = edicionesDeLectura(medidor, lectura, forma, 'Corrección al revisar');
    setErrores(r.errores);
    // ADENDA z61: mientras haya kWh en blanco la tabla NO se autoriza (el botón ya está apagado; esto es por si acaso, y se ve en rojo).
    if (r.enBlanco) {
      setErrores({ ...r.errores, ...faltanEnCeldas() });
      return;
    }
    // hoy85: tampoco con unas fechas con las que el cotizador no puede completar el año (el recuadro rojo dice cuáles), ni con
    // periodos del año que ni están en la tabla (el botón ya está apagado; esto es por si acaso).
    if (bloqueo) return;
    if (Object.keys(r.errores).length) return;
    setAutorizando(true);
    setError(null);
    try {
      // Si la cotización cambió en otra cosa (otra pestaña subió un archivo), se reintenta sola, pero SOLO si la tabla sigue
      // siendo la que la persona revisó: nunca se autoriza una tabla que no vio.
      const { cotizacion } = await ejecutar((c) => api.autorizarLectura(c.id, c.revision, medidor, r.ediciones), { reintentar: (c) => iguales(c.borrador.lecturas[medidor], lectura) });
      const q = cotizacion.borrador.cuestionario;
      const falta = (q.dos_medidores ? (['A', 'B'] as const) : (['A'] as const)).filter((m) => lecturaPorAutorizar(cotizacion.borrador.lecturas[m]));
      // Falta el otro medidor: se pasa a su pestaña (verificación adversarial: la pantalla se quedaba en el A ya autorizado).
      if (falta.length) alFaltarOtro(falta[0]!);
      // Autorizada (y sin otro medidor pendiente): sigue COSTOS, para agregar los costos adicionales que salgan; después
      // Opciones (Diego, junta del 13-oct, segunda tanda: «brincaba a Opciones y se saltaba Costos»).
      else if (cotizacion.borrador.resultado) navegar(`/cotizaciones/${cotizacion.id}/costos`);
    } catch (e) {
      setError(e);
    } finally {
      setAutorizando(false);
    }
  }

  // Tanda 1c: la MISMA ventana del año que la lectura y el motor. En pantalla, solo los meses que cuentan: una cotización
  // guardada con 26 enseña los 12 (los más viejos se dicen en el resumen y se guardan igual, sin tocarlos).
  // Auditoría del 26-oct (hallazgo 14): la facturación REAL, no supuesta por la tarifa: la del recibo (o la que eligió el
  // vendedor); si el recibo no la dice, la que usó el cálculo; sin cálculo, la regla del motor (GDMTO/GDMTH mensual; si no,
  // la del cuestionario).
  const facturacionTabla = facturacionDe(forma);
  const ventana = ventanaDeFilas(forma.filas, facturacionTabla, forma.filas.map((f) => (f.idx !== null ? lectura.historial[f.idx]?.origen : 'manual')));
  const ocultos = new Set(ventana.fuera.filter((i) => forma.filas[i]!.idx !== null));
  // El aviso de los periodos viejos que quitó la lectura (info): JUNTO a la tabla, no solo en «Ver todos».
  const periodosQuitados = cot.borrador.avisos.find((a) => esAvisoDePeriodosQuitados(a, medidor)) ?? null;
  // Tanda 2: los meses que proyectó la IA, JUNTO a la tabla (además de la insignia en su fila y del aviso de «N de 12»).
  const proyectadosIA = mesesProyectados(lectura.historial);
  // Verificación de la tanda 2: si la IA NO pudo proyectar un mes (caída, sin llave, tope de gasto, se negó o cifras fuera
  // del patrón), también JUNTO a la tabla: la falla no se queda en «Ver todos».
  const proyeccionFallida = cot.borrador.avisos.filter((a) => esAvisoDeProyeccionFallida(a, medidor));

  // ADENDA z61: «Agregar periodo» solo en una tabla SIN fechas (el historial impreso de GDMTO/GDMTH): con fechas, los periodos que
  // faltan los pone el cotizador (hacia atrás del último recibo).
  const agregarPeriodo = () =>
    setForma((f) => {
      const { fila, posicion } = filaNueva(f.filas, facturacionTabla);
      return { ...f, filas: [...f.filas.slice(0, posicion), fila, ...f.filas.slice(posicion)] };
    });
  // ADENDA z61: qué falta en el año (lo que está EN PANTALLA: lo que el vendedor ya tecleó cuenta) y los dos botones. Nada se guarda solo.
  const plan = useMemo(() => (puedeEditar ? planDeLaTabla(forma, lectura, contexto(forma)) : null), [puedeEditar, forma, lectura, q, cot.borrador.resultado?.facturacion]); // contexto() solo lee q y la facturación del cálculo
  const enBlanco = filasEnBlanco(forma.filas, ocultos);
  const aviso = puedeEditar ? avisoDeMeses(plan, forma.filas, enBlanco, facturacionTabla, !!pedirSugerencia(forma, lectura, contexto(forma))) : null;
  // hoy85 (verificador de hoy84): si el cotizador no puede completar el año por unas fechas que el cálculo tampoco acepta, se dice
  // en rojo junto a la tabla y no se autoriza así (antes se saltaba en silencio y «Autorizar» quedaba prendido).
  const sinPlan = useMemo(() => (puedeEditar && !plan && lecturaDeRecibos(lectura) ? avisoSinPlan(forma, lectura, contexto(forma), porAutorizar) : null), [puedeEditar, plan, forma, lectura, q, cot.borrador.resultado?.facturacion, porAutorizar]); // contexto() solo lee q y la facturación del cálculo
  const revisar = sinPlan ? new Set(sinPlan.indices) : null;
  // hoy85 (z62(1)): por qué el PDF imprime como recibo los meses escritos a mano (la MISMA regla del PDF; del cálculo guardado).
  // hoy85 vuelta 3: también en la pestaña del B con dos medidores (antes solo el A: el B decía «en el PDF va como estimado» y no iba).
  const comoRecibo = medidor === 'A' || cot.borrador.cuestionario.dos_medidores ? porqueEscritosVanComoRecibo(cot.borrador) : null;
  const sinReciboEnTabla = forma.filas.filter((f, i) => !ocultos.has(i) && filaSinRecibo(f, f.idx !== null ? (lectura.historial[f.idx] ?? null) : null, lectura)).length;
  // hoy85 (verificadores: «nunca autorizar sin los 12 meses»): una sola regla de por qué no se autoriza (kWh en blanco, periodos que
  // ni están en la tabla, fechas que no dejan completar el año). Una tabla ya autorizada solo se detiene por los kWh en blanco (z61).
  const porque = puedeEditar ? bloqueoDelAnio(lecturaDeRecibos(lectura) ? plan : null, enBlanco.length, sinPlan, facturacionTabla) : null;
  const bloqueo = porque && (porAutorizar || enBlanco.length) ? porque.corto : null;
  /** Las celdas de kWh en blanco, en rojo (al intentar autorizar o guardar una tabla autorizada con meses en blanco). */
  function faltanEnCeldas(): ErroresLectura {
    return Object.fromEntries(filasEnBlanco(forma.filas, ocultos).map((i) => [`fila.${i}.kwh`, 'Falta: escribe sus kWh o usa «Sugerir con IA»']));
  }
  const [completadoTexto, setCompletadoTexto] = useState<string | null>(null);
  const [completadoAmarillo, setCompletadoAmarillo] = useState(false);
  if (completadoTexto && !hayCambios) setCompletadoTexto(null);
  // hoy86 · ADENDA z64-2: en un negocio PDBT «Sugerir con IA» le pregunta a la IA (pedirSugerencia). Mientras contesta, el botón
  // dice «Sugiriendo…» y la tabla no se toca; si la IA no sirve, no contesta en ESPERA_SUGERIR_MS o el vendedor cambió la tabla
  // mientras tanto, se pone la curva de Sonora y se dice en AMARILLO (nunca deja meses vacíos callado).
  const [sugiriendo, setSugiriendo] = useState(false);
  // Aparte de completadoTexto, que se borra solo si la tabla no tiene cambios (y aquí no se puso nada).
  const [esperaOtraOperacion, setEsperaOtraOperacion] = useState<string | null>(null);
  const formaActual = useRef(forma);
  formaActual.current = forma;
  async function sugerir() {
    const f = forma;
    const ctx = contexto(f);
    const pedido = pedirSugerencia(f, lectura, ctx);
    let ia: SugerenciaIA | undefined;
    setEsperaOtraOperacion(null);
    if (pedido) {
      setSugiriendo(true);
      const corte = new AbortController();
      const reloj = setTimeout(() => corte.abort(), ESPERA_SUGERIR_MS);
      try {
        const r = await api.sugerirConsumos(cot.id, pedido.cuerpo, corte.signal);
        ia = r.fuente === 'curva' ? { tipo: 'fallo', motivo: r.motivo } : { tipo: 'ia', kwh: r.kwh, simulada: r.fuente === 'ia_simulada', nota: r.nota, periodos: periodosPedidos(pedido.porEstimar) };
      } catch (e) {
        // hoy86 (verificación r2): otra operación de esta cotización (la IA, el PDF) sigue corriendo: es esperar, no un fallo de la IA.
        // No se llena la tabla con la curva: el vendedor vuelve a apretar cuando termine.
        if (e instanceof ErrorDeApi && e.codigo === 'OPERACION_EN_CURSO') {
          setEsperaOtraOperacion(`${e.message} Después vuelve a apretar «Sugerir con IA».`);
          return;
        }
        ia = { tipo: 'fallo', motivo: e instanceof DOMException && e.name === 'AbortError' ? `no contestó en ${Math.round(ESPERA_SUGERIR_MS / 1000)} segundos` : e instanceof ErrorDeApi ? e.message : e instanceof Error ? e.message : String(e) };
      } finally {
        clearTimeout(reloj);
        setSugiriendo(false);
      }
      // El vendedor cambió la tabla mientras la IA pensaba: se respeta lo que tecleó (la IA contestó para otra tabla).
      if (formaActual.current !== f) {
        setCompletadoTexto('Cambiaste la tabla mientras la IA pensaba: no se puso nada. Vuelve a apretar «Sugerir con IA».');
        setCompletadoAmarillo(true);
        return;
      }
    }
    const r = sugerirConIA(f, lectura, ctx, ia);
    if (!r) return;
    setForma(r.forma);
    setErrores({});
    setCompletadoTexto(r.texto);
    setCompletadoAmarillo(r.fuente === 'curva_por_falla');
  }

  // hoy86 (verificación de la 6.ª vuelta): una casa con meses que la IA estimó cuando era negocio: el PDF no sale así (rojo MESES_IA_EN_CASA).
  // Aquí se arregla: el botón los llena con la curva, y si no hay meses reales con que estimar, los escribe el vendedor.
  const deIAEnCasa = puedeEditar && formatoPara(q) === 'residencial' ? mesesDeLaIAEnLaTabla(forma, lectura) : [];
  const conCurva = deIAEnCasa.length ? conCurvaEnLugarDeIA(forma, lectura, contexto(forma)) : null;
  function llenarConCurva() {
    if (!conCurva) return;
    setForma(conCurva.forma);
    setErrores({});
    setCompletadoTexto(conCurva.texto);
    setCompletadoAmarillo(false);
  }

  return (
    <div className="pila">
      <div className="lectura__estado">
        <Chip tono={estado.tono}>{estado.texto}</Chip>
        {porAutorizar && <Chip tono="ambar">Por autorizar</Chip>}
        <span className="nota-suave">
          {aMano || !lectura.archivo_ids.length ? 'Capturada a mano' : `De ${lectura.archivo_ids.length} ${lectura.archivo_ids.length === 1 ? 'archivo' : 'archivos'}`}
          {amarillos > 0 && ` · ${amarillos} ${amarillos === 1 ? 'dato dudoso' : 'datos dudosos'} en amarillo`}
        </span>
      </div>
      {!porAutorizar && lectura.autorizada ? (
        <p className="autorizada">
          <BadgeCheck aria-hidden /> Tabla autorizada por {lectura.autorizada.nombre} · {fechaHoraCorta(lectura.autorizada.en)}
        </p>
      ) : null}
      {lectura.notas_ia && <p className="nota-ia">{lectura.notas_ia}</p>}

      <div className="rejilla-datos">
        <CampoSelect
          etiqueta="Tarifa"
          value={forma.tarifa}
          disabled={!puedeEditar}
          onChange={(e) => setForma({ ...forma, tarifa: e.target.value as FormaLectura['tarifa'] })}
          opciones={OPCIONES_TARIFA}
          error={errores.tarifa}
          className={esDudoso('/tarifa') ? 'campo--dudoso' : ''}
        />
        <CampoSelect
          etiqueta="Facturación"
          value={forma.facturacion}
          disabled={!puedeEditar}
          onChange={(e) => setForma({ ...forma, facturacion: e.target.value as FormaLectura['facturacion'] })}
          opciones={[
            { valor: '', texto: 'Sin leer' },
            { valor: 'mensual', texto: 'Mensual' },
            { valor: 'bimestral', texto: 'Bimestral' },
          ]}
        />
        {/* ADENDA z61: el total a pagar es el que imprime el recibo: no se escribe a mano (si se leyó mal, vuelve a leer el recibo). */}
        <CampoTexto etiqueta="Total a pagar" inputMode="decimal" sufijo="$" disabled value={forma.total} onChange={() => undefined} className={esDudoso('/total_a_pagar') ? 'campo--dudoso' : ''} />
        {mt && (
          <CampoTexto
            etiqueta="Demanda contratada"
            inputMode="decimal"
            sufijo="kW"
            disabled={!puedeEditar}
            value={forma.demandaContratada}
            onChange={(e) => setForma({ ...forma, demandaContratada: e.target.value })}
            error={errores.demandaContratada}
            className={esDudoso('/demanda_contratada_kw') ? 'campo--dudoso' : ''}
          />
        )}
      </div>

      <CartelFacturacion facturacion={facturacionTabla} leida={!!forma.facturacion} lectura={lectura} />
      {proyectadosIA.length > 0 && (
        <p className="nota-ia" role="note">
          Proyectado con IA: {proyectadosIA.join(', ')}. {proyectadosIA.length === 1 ? 'Ese mes no tiene su recibo y la IA lo completó' : 'Esos meses no tienen su recibo y la IA los completó'} con el patrón de los otros recibos (el pago se estima con las cuotas de CFE). Revísalo y corrígelo en la tabla si tienes el dato.
        </p>
      )}
      {aviso && (
        <div className="completar-anio" role="region" aria-label="Meses que faltan">
          <div>
            <p className="completar-anio__titulo">{aviso.titulo}</p>
            <ul className="completar-anio__detalle">
              {aviso.detalle.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </div>
          {(aviso.sugerir || aviso.poner) && (
            <div className="completar-anio__botones">
              {aviso.sugerir && (
                <Boton variante="principal" icono={<Sparkles aria-hidden />} cargando={sugiriendo} onClick={() => void sugerir()}>
                  {sugiriendo ? 'Sugiriendo…' : 'Sugerir con IA'}
                </Boton>
              )}
              {aviso.poner && (
                <Boton
                  variante="fantasma"
                  icono={<CalendarPlus aria-hidden />}
                  onClick={() => {
                    const r = conMesesQueFaltan(forma, lectura, contexto(forma));
                    if (r) setForma(r.forma);
                  }}
                >
                  Poner los meses que faltan
                </Boton>
              )}
            </div>
          )}
        </div>
      )}
      {deIAEnCasa.length > 0 && (
        <div className="completar-anio completar-anio--rojo" role="alert" aria-label="Meses que estimó la IA para un negocio">
          <div>
            <p className="completar-anio__titulo">{deIAEnCasa.length === 1 ? 'Este periodo lo estimó la IA para un negocio' : `Estos ${deIAEnCasa.length} periodos los estimó la IA para un negocio`}</p>
            <ul className="completar-anio__detalle">
              <li>Esta cotización ya es de casa y así el PDF no sale: en una casa van con la curva de Sonora. {conCurva ? `Aprieta «${BOTON_CURVA_EN_CASA}» y guarda, o escribe sus kWh.` : 'No hay meses reales con que estimarlos: escribe sus kWh.'}</li>
            </ul>
          </div>
          {conCurva && (
            <div className="completar-anio__botones">
              <Boton variante="principal" icono={<Sparkles aria-hidden />} onClick={llenarConCurva}>
                {BOTON_CURVA_EN_CASA}
              </Boton>
            </div>
          )}
        </div>
      )}
      {sinPlan && (
        <div className="completar-anio completar-anio--rojo" role="alert" aria-label="El año no se puede completar">
          <div>
            <p className="completar-anio__titulo">{sinPlan.titulo}</p>
            <ul className="completar-anio__detalle">
              {sinPlan.detalle.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {esperaOtraOperacion && (
        <p className="aviso-ancho aviso-ancho--ambar" role="alert">
          {esperaOtraOperacion}
        </p>
      )}
      {completadoTexto && (
        <p className={completadoAmarillo ? 'aviso-ancho aviso-ancho--ambar' : 'nota-ia'} role={completadoAmarillo ? 'alert' : 'note'}>
          {completadoTexto}
        </p>
      )}
      {proyeccionFallida.map((a) => (
        <p key={a.id} className="nota-ia" role="note">
          {a.mensaje}
        </p>
      ))}
      <TablaHistorial facturacion={facturacionTabla} filas={forma.filas} setFilas={(filas) => setForma({ ...forma, filas })} errores={errores} esDudoso={esDudoso} original={lectura} mediaTension={mt} puedeEditar={puedeEditar} periodos={periodosDelMedidor} ocultos={ocultos} cuentan={new Set(ventana.dentro)} baseFutura={medidor === 'A' && resultado?.base_futura ? { tarifa: resultado.tarifa, aumento: resultado.base_futura.aumento !== null } : null} revisar={revisar} pdfComoRecibo={comoRecibo} />
      <div className="fila-botones fila-botones--entre">
        {/* Verificación de la tanda 1: sin periodo leído no se supone mensual; en GDMTO/GDMTH sí lo es (lo dice la tarifa). */}
        <ResumenFilas filas={forma.filas} facturacion={facturacionTabla} ventana={ventana} estimados={forma.filas.filter((f, i) => { const o = f.idx !== null ? (lectura.historial[f.idx] ?? null) : null; return !ocultos.has(i) && (filaEstimada(f, o) || filaSinRecibo(f, o, lectura)); }).length} />
        {puedeEditar && agregarPeriodoHaceFalta(forma.filas, plan) && (
          <Boton chico icono={<Plus aria-hidden />} title="Agrega el mes que va antes del más viejo (escribe su mes como lo imprime CFE)" onClick={agregarPeriodo}>
            Agregar periodo
          </Boton>
        )}
      </div>
      {comoRecibo && sinReciboEnTabla > 0 && (
        // hoy85 (verificador del vendedor): la tabla dice «sin recibo · estimado», pero en ESTOS casos el PDF los imprime como recibo
        // (como antes): se le dice al vendedor, no solo a Diego en la nota del PDF.
        <p className="aviso-ancho aviso-ancho--ambar" role="note" aria-label="Meses a mano en el PDF">
          <strong>En el PDF van como recibo.</strong> {sinReciboEnTabla === 1 ? `El ${facturacionTabla === 'bimestral' ? 'bimestre' : 'mes'} que escribiste sin recibo va` : `Los ${sinReciboEnTabla} ${facturacionTabla === 'bimestral' ? 'bimestres' : 'meses'} que escribiste sin recibo van`} como RECIBO (como antes): {comoRecibo}. Sube el recibo de {sinReciboEnTabla === 1 ? 'ese periodo' : 'esos periodos'} si el cliente lo tiene, o cotízalo con Diego.
        </p>
      )}
      {periodosQuitados && (
        <p className="nota-suave" role="note">
          {periodosQuitados.mensaje}
        </p>
      )}

      {/* Etapa 4 · verificación adversarial: el botón de autorizar va DESPUÉS de los meses y del total (antes quedaba arriba
          de la tabla e invitaba a autorizar sin ver un solo mes en el celular). */}
      {porAutorizar && (
        <div className="autorizar" role="region" aria-label="Autorizar la tabla">
          <div className="autorizar__texto">
            {porque ? (
              <>
                <p className="autorizar__titulo">{porque.titulo}</p>
                <p className="autorizar__detalle">{porque.detalle}</p>
              </>
            ) : (
              <>
                <p className="autorizar__titulo">Falta autorizar esta tabla</p>
                <p className="autorizar__detalle">
                  ¿Coincide con el recibo{amarillos ? ', sobre todo lo amarillo' : ''}? Si algo no cuadra, corrige los kWh arriba. Las opciones se calculan cuando la autorices.
                </p>
              </>
            )}
          </div>
          {puedeEditar && (
            <div className="autorizar__botones">
              <Boton variante="principal" icono={<BadgeCheck aria-hidden />} cargando={autorizando} disabled={guardando || !!bloqueo} onClick={() => void autorizar()}>
                {hayCambios ? 'Guardar y autorizar' : 'Autorizar tabla'}
              </Boton>
              {hayCambios && (
                <Boton variante="fantasma" icono={<Save aria-hidden />} cargando={guardando} disabled={autorizando} onClick={() => void guardar()}>
                  Solo guardar
                </Boton>
              )}
            </div>
          )}
        </div>
      )}

      {lectura.precios_energia.length > 0 && (
        <details className="plegable">
          <summary>Precios por kWh del último periodo</summary>
          <table className="tabla tabla--compacta">
            <tbody>
              {lectura.precios_energia.map((p, i) => (
                <tr key={i}>
                  <th scope="row">{ETIQUETA_BLOQUE[p.bloque] ?? p.bloque}</th>
                  <td className="num">{p.kwh === null ? '—' : fmtKwh(p.kwh)}</td>
                  <td className="num">${p.precio_kwh.toFixed(3)} / kWh</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
      {lectura.cargos_ultimo_periodo && (
        // En amarillo y abierto si no cuadra (verificación adversarial: «1 dato dudoso en amarillo» y nada amarillo).
        <details className={['plegable', desgloseDudoso ? 'plegable--dudoso' : ''].join(' ')} open={desgloseDudoso || undefined}>
          <summary>Desglose del último recibo{desgloseDudoso ? ' · revísalo' : ''}</summary>
          {desgloseDudoso && <p className="plegable__nota">Estas cifras no cuadran entre sí o no se leyeron con seguridad: compáralas con el recibo (la nota de arriba dice cuál).</p>}
          <table className="tabla tabla--compacta">
            <tbody>
              {Object.entries(lectura.cargos_ultimo_periodo)
                .filter(([, v]) => v !== null)
                .map(([k, v]) => (
                  <tr key={k}>
                    <th scope="row">{ETIQUETA_CARGO[k] ?? k.replace(/_/g, ' ')}</th>
                    <td className="num">{dinero(v as number, false)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </details>
      )}

      <details className="plegable plegable--privado" open={privadosDudosos > 0 || undefined}>
        <summary>
          <Lock aria-hidden /> Datos privados del recibo (nunca salen en el PDF){privadosDudosos > 0 ? ` · ${privadosDudosos} en amarillo` : ''}
        </summary>
        <div className="rejilla-datos">
          {PRIVADOS.map(({ k, t }) => (
            <CampoTexto
              key={k}
              etiqueta={t}
              disabled={!puedeEditar}
              value={forma.privados[k]}
              className={esDudoso(`/privados/${k}`) ? 'campo--dudoso' : ''}
              onChange={(e) => setForma({ ...forma, privados: { ...forma.privados, [k]: e.target.value } })}
            />
          ))}
        </div>
      </details>

      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      {Object.keys(errores).length > 0 && <p className="campo__error">Hay datos que no sirven en la tabla (marcados en rojo).</p>}
      {puedeEditar && hayCambios && (
        <div className="barra-guardar" role="region" aria-label="Cambios sin guardar">
          <span>
            {cambiosVisibles === 1 ? '1 cambio sin guardar' : `${cambiosVisibles} cambios sin guardar`}
            <span className="barra-guardar__detalle">{bloqueo ? ` · ${bloqueo.toLowerCase()} para ${porAutorizar ? 'autorizar' : 'guardar'}` : porAutorizar ? ' · al autorizar se calculan las opciones' : ' · al guardar se recalculan las opciones'}</span>
          </span>
          <div className="fila-botones">
            <Boton
              chico
              variante="fantasma"
              icono={<RotateCcw aria-hidden />}
              onClick={() => {
                setForma(formaInicial);
                setErrores({});
              }}
            >
              Descartar
            </Boton>
            {porAutorizar ? (
              // En el celular la barra solo trae dos botones (como siempre); «Solo guardar» vive en el recuadro de arriba.
              <Boton chico variante="principal" icono={<BadgeCheck aria-hidden />} cargando={autorizando} disabled={guardando || !!bloqueo} onClick={() => void autorizar()}>
                Guardar y autorizar
              </Boton>
            ) : (
              <Boton chico variante="principal" icono={<Save aria-hidden />} cargando={guardando} disabled={!!bloqueo} onClick={() => void guardar()}>
                Guardar
              </Boton>
            )}
          </div>
        </div>
      )}
      <p className="nota-suave">Última actualización: {fechaHoraCorta(cot.actualizada_en)}</p>
    </div>
  );
}

function CapturaManual({ medidor, recibos }: { medidor: Medidor; recibos: ArchivoSubido[] }) {
  const { ejecutar, cot } = usarCotizacion();
  const q = cot.borrador.cuestionario;
  const bimestral = q.facturacion === 'bimestral';
  const [forma, setForma] = useState<FormaLectura>(() => ({
    tarifa: q.tarifa === 'no_se' ? '' : q.tarifa,
    facturacion: q.facturacion === 'no_se' ? '' : q.facturacion,
    total: '',
    demandaContratada: '',
    cargaConectada: '',
    filas: filasParaCapturar(fechaHermosillo(), bimestral),
    privados: { titular: '', numero_servicio: '', direccion: '', rmu: '', cuenta: '', numero_medidor: '' },
  }));
  const [errores, setErrores] = useState<ErroresLectura>({});
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const mt = forma.tarifa === 'GDMTO' || forma.tarifa === 'GDMTH';

  async function guardar() {
    const r = lecturaManual(medidor, recibos.map((a) => a.id), forma);
    setErrores(r.errores);
    if (!r.lectura) return;
    setGuardando(true);
    setError(null);
    try {
      // «antes» = lo que la persona vio (no lo de una recarga): si alguien leyó los recibos mientras tanto, el servidor lo
      // rechaza en vez de pisar esa lectura con la captura.
      const antes = cot.borrador.lecturas[medidor];
      await ejecutar((c) => api.editarBorrador(c.id, c.revision, [{ op: 'reemplazar', ruta: `/lecturas/${medidor}`, antes, despues: r.lectura, motivo: 'Captura a mano' }]), { reintentar: true });
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="pila">
      <p className="nota-suave">
        Copia del recibo el consumo (kWh) de cada periodo; el pago lo calcula el cotizador con la tarifa. Los periodos que dejes vacíos no cuentan. Al guardar, la tabla queda autorizada a tu nombre y se calculan las opciones.
      </p>
      {/* z35-4: el mismo cartel MENSUAL/BIMESTRAL que con el recibo leído, con lo que se elige en «Facturación» */}
      <CartelFacturacion facturacion={forma.facturacion === '' ? null : forma.facturacion} leida={forma.facturacion !== ''} lectura={null} />
      <div className="rejilla-datos">
        <CampoSelect etiqueta="Tarifa" value={forma.tarifa} onChange={(e) => setForma({ ...forma, tarifa: e.target.value as FormaLectura['tarifa'] })} opciones={[{ valor: '', texto: 'Elige' }, ...TARIFAS.map((t) => ({ valor: t, texto: t }))]} error={errores.tarifa} />
        <CampoSelect
          etiqueta="Facturación"
          value={forma.facturacion}
          onChange={(e) => {
            const f = e.target.value as FormaLectura['facturacion'];
            setForma({ ...forma, facturacion: f, filas: forma.filas.some((x) => x.kwh) ? forma.filas : filasParaCapturar(fechaHermosillo(), f === 'bimestral') });
          }}
          opciones={[
            { valor: '', texto: 'Elige' },
            { valor: 'mensual', texto: 'Mensual' },
            { valor: 'bimestral', texto: 'Bimestral' },
          ]}
          error={errores.facturacion}
        />
      </div>
      <TablaHistorial facturacion={forma.facturacion || null} filas={forma.filas} setFilas={(filas) => setForma({ ...forma, filas })} errores={errores} esDudoso={() => false} original={null} mediaTension={mt} puedeEditar />
      {errores.filas && <p className="campo__error">{errores.filas}</p>}
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      <div className="fila-botones fila-botones--fin">
        <Boton variante="principal" icono={<Save aria-hidden />} cargando={guardando} onClick={() => void guardar()}>
          Guardar consumos
        </Boton>
      </div>
    </div>
  );
}

function CorregirConIA() {
  const { cot, ejecutar } = usarCotizacion();
  const { config } = usarUsuario();
  const [instruccion, setInstruccion] = useState('');
  const [pidiendo, setPidiendo] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [propuesta, setPropuesta] = useState<Omit<ResultadoCorregir, 'uso'> | null>(null);
  const [elegidas, setElegidas] = useState<Set<number>>(new Set());
  const [error, setError] = useState<unknown>(null);
  const sinLlave = config.ia.modo === 'sin_llave';

  const prueba = useMemo(() => {
    if (!propuesta) return null;
    const eds = propuesta.ediciones.filter((_, i) => elegidas.has(i));
    if (!eds.length) return null;
    try {
      aplicarEdiciones(cot.borrador, eds);
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : String(e);
    }
  }, [propuesta, elegidas, cot.borrador]);

  // z25: las ediciones del ajuste de consumos se ven como tabla antes/después (una casilla para todas), no una por una.
  const ajuste = propuesta?.ajuste_consumos ?? null;
  const delAjuste = useMemo(() => {
    if (!propuesta || !ajuste) return new Set<number>();
    const rutas = new Set(ajuste.meses.map((m) => `/lecturas/${ajuste.medidor}/historial/${m.indice}`));
    return new Set(propuesta.ediciones.flatMap((e, i) => (rutas.has(e.ruta) ? [i] : [])));
  }, [propuesta, ajuste]);
  const ajusteElegido = delAjuste.size > 0 && [...delAjuste].every((i) => elegidas.has(i));

  async function pedir() {
    setPidiendo(true);
    setError(null);
    setPropuesta(null);
    try {
      const r = await ejecutar((c) => api.corregir(c.id, c.revision, instruccion.trim()));
      setPropuesta(r.propuesta);
      setElegidas(new Set(r.propuesta.ediciones.map((_, i) => i)));
    } catch (e) {
      setError(e);
    } finally {
      setPidiendo(false);
    }
  }

  async function aplicar() {
    if (!propuesta) return;
    const eds: EdicionCampo[] = propuesta.ediciones.filter((_, i) => elegidas.has(i));
    setAplicando(true);
    setError(null);
    try {
      await ejecutar((c) => api.editarBorrador(c.id, c.revision, eds), { reintentar: true });
      setPropuesta(null);
      setInstruccion('');
    } catch (e) {
      setError(e);
    } finally {
      setAplicando(false);
    }
  }

  return (
    <div className={['corregir', sinLlave ? 'corregir--sin' : ''].join(' ')}>
      <div className="corregir__fila">
        <CampoArea
          etiqueta={
            <>
              <Sparkles aria-hidden className="corregir__chispa" /> Pídele a la IA que corrija
            </>
          }
          rows={3}
          maxLength={1000}
          disabled={sinLlave}
          placeholder={sinLlave ? 'La IA no tiene llave: corrige directo en la tabla.' : PISTA_CORREGIR_IA}
          value={instruccion}
          onChange={(e) => setInstruccion(e.target.value)}
          ayuda={sinLlave ? undefined : AYUDA_CORREGIR_IA}
        />
        <Boton variante="noche" icono={<Sparkles aria-hidden />} cargando={pidiendo} disabled={sinLlave || instruccion.trim().length < 3} onClick={() => void pedir()}>
          Proponer cambio
        </Boton>
      </div>
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      {propuesta && (
        <div className="propuesta" aria-live="polite">
          <p className="propuesta__explicacion">{propuesta.explicacion}</p>
          {/* z48-10: lo que del ajuste no se puede aplicar, en rojo; la curva de Sonora que reemplazó bloques, a la vista */}
          {propuesta.avisos.filter((a) => a.nivel === 'rojo').map((a, i) => (
            <p key={`r${i}`} className="campo__error" role="alert">{a.mensaje}</p>
          ))}
          {propuesta.avisos.filter((a) => a.nivel === 'info' && a.mensaje.startsWith(PREFIJO_CURVA_SONORA)).map((a, i) => (
            <p key={`c${i}`} className="propuesta__explicacion">{a.mensaje}</p>
          ))}
          {ajuste && delAjuste.size > 0 && (
            <TablaAjusteConsumos
              ajuste={ajuste}
              elegido={ajusteElegido}
              alCambiar={(v) => {
                const n = new Set(elegidas);
                for (const i of delAjuste) {
                  if (v) n.add(i);
                  else n.delete(i);
                }
                setElegidas(n);
              }}
            />
          )}
          {propuesta.ediciones.length > 0 && (
            <>
              <ul className="propuesta__lista">
                {propuesta.ediciones.map((e, i) => delAjuste.has(i) ? null : (
                  <li key={i}>
                    <label className="propuesta__cambio">
                      <input
                        type="checkbox"
                        checked={elegidas.has(i)}
                        onChange={(ev) => {
                          const n = new Set(elegidas);
                          if (ev.target.checked) n.add(i);
                          else n.delete(i);
                          setElegidas(n);
                        }}
                      />
                      <span className="propuesta__ruta">{etiquetaRuta(e.ruta, cot.borrador)}</span>
                      <span className="propuesta__valores">
                        <del>{valorCorto(e.antes, e.ruta)}</del> → <ins>{valorCorto(e.despues, e.ruta)}</ins>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
              {prueba && (
                <p className="campo__error" role="alert">
                  Ese cambio ya no se puede aplicar: {prueba}
                </p>
              )}
            </>
          )}
          <div className="fila-botones">
            <Boton chico variante="fantasma" onClick={() => setPropuesta(null)}>
              Descartar
            </Boton>
            {propuesta.ediciones.length > 0 && (
              <Boton chico variante="principal" cargando={aplicando} disabled={elegidas.size === 0 || prueba !== null} onClick={() => void aplicar()}>
                Aplicar {elegidas.size === 1 ? '1 cambio' : `${elegidas.size} cambios`}
              </Boton>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Estado de CADA recibo de este medidor según la última lectura (repetido, no es de CFE, no se lee…). Abierto si
 *  algo pide acción; si todo se leyó, plegado con su resumen. */
function ArchivosLeidos({ recibos, alVer, visto }: { recibos: ArchivoSubido[]; alVer: (id: string) => void; visto: string | null }) {
  const { cot } = usarCotizacion();
  const b = cot.borrador;
  if (!b.archivos_leidos || b.archivos_leidos.length === 0 || recibos.length === 0) return null;
  const numeroDe = (id: string) => {
    const i = recibos.findIndex((a) => a.id === id);
    return i < 0 ? null : i + 1;
  };
  const filas = recibos.map((a, i) => ({ a, n: i + 1, estado: estadoDeArchivo(b, a) }));
  const despues = filas.filter((f) => f.estado.tipo === 'despues').length;
  const cuenta = cuentaPorEstado(filas.flatMap((f) => (f.estado.tipo === 'leido' ? [f.estado.leido] : [])));
  const piden = despues > 0 || filas.some((f) => f.estado.tipo === 'leido' && f.estado.leido.estado !== 'leido');
  const resumen = resumenEstados(cuenta, despues);
  return (
    <details className="plegable archivos-leidos" open={piden || undefined}>
      <summary>
        <ChevronDown aria-hidden className="plegable__flecha" />
        <span>
          {recibos.length === 1 ? '1 archivo' : `${recibos.length} archivos`}
          <span className="archivos-leidos__resumen"> · {resumen}</span>
        </span>
      </summary>
      <ol className="archivos-leidos__lista">
        {filas.map(({ a, n, estado }) => {
          const t = textoEstado(estado, numeroDe);
          const l = estado.tipo === 'leido' ? estado.leido : null;
          return (
            <li key={a.id}>
              <button type="button" className={['archivo-leido', visto === a.id ? 'es-activo' : ''].join(' ')} onClick={() => alVer(a.id)} aria-label={`Recibo ${n}, ${a.nombre_original}: ${t?.texto ?? 'sin estado'}. Verlo en el visor.`}>
                <span className="archivo-leido__numero">{n}</span>
                <span className="archivo-leido__datos">
                  <span className="archivo-leido__nombre">{a.nombre_original}</span>
                  {(l?.periodo || (l?.nota && l.estado !== 'leido') || estado.tipo === 'despues') && (
                    <span className="archivo-leido__nota">
                      {l?.periodo ? periodoCorto(l.periodo.inicio, l.periodo.fin) : null}
                      {l?.periodo && l.nota && l.estado !== 'leido' ? ' · ' : null}
                      {l?.nota && l.estado !== 'leido' ? l.nota : null}
                      {estado.tipo === 'despues' ? 'Vuelve a leer los recibos para incluirlo.' : null}
                    </span>
                  )}
                </span>
                {t && <Chip tono={t.tono}>{t.texto}</Chip>}
              </button>
            </li>
          );
        })}
      </ol>
    </details>
  );
}

/** El medidor que se ve lo lleva el espacio de la cotización: la franja «Falta autorizar la tabla del medidor B» cambia a
 *  esa pestaña antes de llevar a la tabla (verificación adversarial de la etapa 4: «Ir a la tabla» no hacía nada con el B
 *  pendiente en otra pestaña). `irTabla` cambia cada vez que se pide ir a la tabla. */
/** Recibos subidos que todavía no se leen (o se subieron después de la lectura): «Leer recibos» a la vista, arriba de la
 *  tabla (lo normal es que se lean solos al terminar de subir; esto cubre lo que quedó pendiente). */
function LeerPendientes() {
  const { cot, lectura } = usarCotizacion();
  const n = recibosSinLeer(cot.borrador).length;
  if (!n && !lectura.leyendo) return null;
  return (
    <section className="leer-ia" aria-label="Recibos sin leer">
      <div className="leer-ia__texto">
        <p className="leer-ia__titulo">{lectura.leyendo ? 'Leyendo los recibos…' : n === 1 ? 'Hay 1 recibo sin leer' : `Hay ${n} recibos sin leer`}</p>
        <p className="leer-ia__detalle">
          {lectura.esperandoOtra
            ? 'Ya se está leyendo esta cotización en otra pestaña u otro teléfono (o sacando su PDF): en cuanto termine, se leen los que faltan.'
            : lectura.leyendo
            ? 'La tabla se actualiza al terminar.'
            : 'Léelos para que la tabla los incluya. Los PDF de CFE se leen sin IA, gratis y en segundos.'}
        </p>
      </div>
      <div className="leer-ia__botones">
        <Boton variante="principal" icono={<ScanText aria-hidden />} cargando={lectura.leyendo} disabled={lectura.subiendo} onClick={() => void lectura.leer()}>
          {lectura.leyendo ? 'Leyendo…' : 'Leer recibos'}
        </Boton>
      </div>
      <ErrorEnLinea error={lectura.error} alCerrar={lectura.limpiarError} />
    </section>
  );
}

export function SeccionRevision({ medidor: medidorPedido, alCambiarMedidor, irTabla }: { medidor: Medidor; alCambiarMedidor: (m: Medidor) => void; irTabla: number }) {
  const { cot } = usarCotizacion();
  const { puedeEn } = usarUsuario();
  const puedeEditar = puedeEn('editar_cotizaciones', cot);
  const b = cot.borrador;
  const q = b.cuestionario;
  const medidores: Medidor[] = q.dos_medidores ? ['A', 'B'] : ['A'];
  const medidor: Medidor = medidores.includes(medidorPedido) ? medidorPedido : 'A';
  const setMedidor = alCambiarMedidor;
  const irVisto = useRef(irTabla);
  useEffect(() => {
    if (irTabla === irVisto.current) return;
    irVisto.current = irTabla;
    irALaTabla();
  }, [irTabla, medidor]);
  const recibos = b.archivos.filter((a) => a.clase === 'recibo' && !a.eliminado && a.medidor === medidor);
  const [visto, setVisto] = useState<string | null>(recibos[recibos.length - 1]?.id ?? null);
  const [visorAbierto, setVisorAbierto] = useState(false);
  const archivo = recibos.find((a) => a.id === visto) ?? recibos[recibos.length - 1] ?? null;
  const lecturaGuardada = b.lecturas[medidor];
  const lectura = lecturaConDatos(lecturaGuardada) ? lecturaGuardada : null;

  if (q.consumo.es_nuevo) {
    const e = q.estimacion;
    return (
      <Seccion titulo="Consumo estimado" descripcion="Cliente nuevo: no hay recibos. El cálculo usa esta estimación.">
        <div className="hoja pila">
          {e ? (
            <dl className="resumen__lista">
              <div className="resumen__fila">
                <dt>Método</dt>
                <dd>{e.metodo === 'pago' && e.pago ? fuenteDePago(e.pago) : e.metodo === 'kwh_mes' ? 'kWh al mes' : e.metodo === 'cuadro_cargas' ? 'Cuadro de cargas' : 'Negocios parecidos'}</dd>
              </div>
              {e.kwh_mes !== null && (
                <div className="resumen__fila">
                  <dt>Consumo</dt>
                  <dd>{fmtKwh(e.kwh_mes)} al mes</dd>
                </div>
              )}
              {e.cargas.map((c, i) => (
                <div className="resumen__fila" key={i}>
                  <dt>{c.equipo}</dt>
                  <dd>
                    {c.cantidad} × {numero(c.w)} W · {c.horas_dia} h/día · {c.meses_uso} meses
                  </dd>
                </div>
              ))}
              <div className="resumen__fila">
                <dt>Fuente</dt>
                <dd>{e.fuente}</dd>
              </div>
            </dl>
          ) : (
            <p className="campo__error">Falta la estimación.</p>
          )}
          {puedeEditar && (
            <Enlace a={`/cotizaciones/${cot.id}/cuestionario/editar`} className="boton boton--secundario">
              Cambiar la estimación
            </Enlace>
          )}
        </div>
      </Seccion>
    );
  }

  return (
    <div className="pila pila--grande">
      {puedeEditar && <LeerPendientes />}
      {medidores.length > 1 && (
        <div className="pestanas" role="tablist" aria-label="Medidor">
          {medidores.map((m) => (
            <button key={m} type="button" role="tab" aria-selected={medidor === m} className={['pestanas__item', medidor === m ? 'es-activo' : ''].join(' ')} onClick={() => { setMedidor(m); setVisto(null); }}>
              Medidor {m}
            </button>
          ))}
        </div>
      )}
      <div className="cotejo">
        <aside className="cotejo__documento" aria-label="Recibo original">
          {recibos.length > 1 && (
            <div className="cotejo__archivos" role="tablist" aria-label="Recibos">
              {recibos.map((a, i) => (
                <button key={a.id} type="button" role="tab" aria-selected={archivo?.id === a.id} className={['pastilla', archivo?.id === a.id ? 'es-activo' : ''].join(' ')} onClick={() => setVisto(a.id)} title={a.nombre_original}>
                  {i + 1}
                </button>
              ))}
            </div>
          )}
          <VisorArchivo archivo={archivo} />
          {archivo && <EnlaceArchivo archivo={archivo} />}
        </aside>
        <div className="cotejo__datos pila pila--grande">
          {puedeEditar && lectura && <CorregirConIA />}
          <Seccion
            titulo={medidores.length > 1 ? `Lo que se leyó · medidor ${medidor}` : 'Lo que se leyó'}
            descripcion={lectura ? 'Compara contra el recibo, corrige lo que haga falta y autoriza la tabla. Lo amarillo no se leyó con seguridad.' : 'Todavía no hay lectura de este medidor.'}
            acciones={
              archivo ? (
                <Boton chico icono={<Eye aria-hidden />} className="solo-celular-flex" onClick={() => setVisorAbierto(true)}>
                  Ver recibo
                </Boton>
              ) : undefined
            }
          >
            <div className="hoja pila">
              <ArchivosLeidos
                recibos={recibos}
                visto={archivo?.id ?? null}
                alVer={(id) => {
                  setVisto(id);
                  // En el celular el recibo no está a la vista: se abre en la hoja aparte.
                  if (typeof window.matchMedia === 'function' && !window.matchMedia('(min-width: 64rem)').matches) setVisorAbierto(true);
                }}
              />
              {lectura ? (
                <EditorLectura
                  key={medidor}
                  medidor={medidor}
                  lectura={lectura}
                  alFaltarOtro={(m) => {
                    setMedidor(m);
                    setVisto(null);
                    if (typeof window.scrollTo === 'function') window.scrollTo({ top: 0 });
                  }}
                />
              ) : puedeEditar ? (
                <CapturaManual key={medidor} medidor={medidor} recibos={recibos} />
              ) : (
                <Vacio titulo="Sin lectura">El vendedor todavía no lee los recibos.</Vacio>
              )}
            </div>
          </Seccion>
        </div>
      </div>
      <Dialogo abierto={visorAbierto} alCerrar={() => setVisorAbierto(false)} titulo={archivo?.nombre_original ?? 'Recibo'} ancho="grande">
        <VisorArchivo archivo={visorAbierto ? archivo : null} />
        {visorAbierto && archivo && <EnlaceArchivo archivo={archivo} />}
      </Dialogo>
    </div>
  );
}

type VentanaDeFilas = ReturnType<typeof ventanaDelAnio> & { renglones: { fin: string | null; etiqueta: string; origen?: string }[] };

/** La ventana del año de lo que está EN PANTALLA: la MISMA regla que la lectura y el motor (ventanaDelAnio, con la fecha de
 *  hoy: un recibo del futuro no ancla; anclan los recibos y los renglones manuales). Tanda 1c. */
function ventanaDeFilas(filas: readonly FilaEditable[], facturacion: 'mensual' | 'bimestral' | null, origenes: readonly (string | undefined)[]): VentanaDeFilas {
  const renglones = filas.map((f, i) => ({ fin: f.fin.trim() || null, etiqueta: f.etiqueta, ...(origenes[i] ? { origen: origenes[i] } : {}) }));
  return { ...ventanaDelAnio(renglones, facturacion, fechaHermosillo()), renglones };
}

/** Resumen de lo que está EN PANTALLA (con las correcciones sin guardar): el mismo total que el renglón «Total» de la tabla
 *  (verificación adversarial de la etapa 4: la tabla decía 17,040 y esta línea 17,029, la de antes de corregir). */
function ResumenFilas({ filas, facturacion, ventana, estimados = 0 }: { filas: readonly FilaEditable[]; facturacion: 'mensual' | 'bimestral' | null; ventana: VentanaDeFilas; estimados?: number }) {
  const bimestral = facturacion === 'bimestral';
  // Diego, 25-oct: «26 periodos · 716,876 kWh en total» en un GDMTH con 11 recibos (una lectura guardada antes de la
  // ventana del año). Cuentan SOLO los del año que se cotiza (12 meses o 6 bimestres, como el motor); los más viejos se
  // dicen, no se enseñan y se quitan al volver a leer los recibos.
  const { dentro, fuera, posteriores, renglones } = ventana;
  // Cuentan los periodos con kWh escrito (también los de 0 kWh, como los meses NETMET en que no se cobró energía).
  const conKwh = (i: number) => filas[i]!.kwh.trim() !== '' && Number.isFinite(Number(filas[i]!.kwh.replace(/,/g, '')));
  const total = dentro.reduce((s, i) => s + (Number(filas[i]!.kwh.replace(/,/g, '')) || 0), 0);
  // Tanda 1c: se cuentan MESES, no renglones (un mes partido en dos renglones es uno: «12 meses», no «13 periodos»).
  const n = periodosDistintos(renglones, dentro.filter(conKwh));
  if (!n) return null;
  const unidad = (k: number) => (facturacion === null ? (k === 1 ? 'periodo' : 'periodos') : bimestral ? (k === 1 ? 'bimestre' : 'bimestres') : k === 1 ? 'mes' : 'meses');
  const viejos = periodosDistintos(renglones, fuera.filter(conKwh));
  const despues = periodosDistintos(renglones, posteriores.filter(conKwh));
  return (
    <p className="nota-suave">
      {n} {unidad(n)}
      {estimados > 0 && ` (${estimados} ${estimados === 1 ? 'estimado' : 'estimados'})`} · {fmtKwh(Math.round(total))} en total · promedio {fmtKwh(Math.round(total / n))} por {facturacion === null ? 'periodo' : bimestral ? 'bimestre' : 'mes'}
      {viejos > 0 &&
        ` · ${viejos} ${unidad(viejos)} más ${viejos === 1 ? 'viejo no cuenta' : 'viejos no cuentan'} (fuera del año que se cotiza) y no se ${viejos === 1 ? 'enseña' : 'enseñan'} en la tabla: se quitan al volver a leer los recibos`}
      {despues > 0 &&
        ` · ${despues} ${unidad(despues)} ${despues === 1 ? 'posterior al último recibo no cuenta' : 'posteriores al último recibo no cuentan'}: revisa su mes (suele ser una mala lectura) o sube su recibo`}
    </p>
  );
}

/** Abre el recibo en otra pestaña (el visor del navegador lo enseña completo, con zoom). */
function EnlaceArchivo({ archivo }: { archivo: ArchivoSubido }) {
  const { url } = usarUrlArchivo(archivo.id);
  return (
    <p className="cotejo__nombre">
      {archivo.nombre_original}
      {url && (
        <a href={url} target="_blank" rel="noopener noreferrer" className="cotejo__abrir">
          <ExternalLink aria-hidden /> Abrir en otra pestaña
        </a>
      )}
    </p>
  );
}
